import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Phone } from 'lucide-react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { Button } from './ui/button';

export const ServicesSection = () => {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-zinc-950 relative overflow-hidden">
      {/* Background robot image - Hidden on mobile */}
      <div 
        className="absolute right-0 top-0 w-1/2 h-full opacity-[0.06] hidden md:block"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1760931969401-9bd6ee902798)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>
      
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/95 to-transparent"></div>

      {/* Decorative tech elements - Hidden on mobile */}
      <div className="absolute top-32 left-32 w-20 h-20 border-2 border-white/5 rounded-none animate-spin hidden lg:block" style={{ animationDuration: '20s' }}></div>
      <div className="absolute bottom-32 right-32 w-16 h-16 border-2 border-white/5 rounded-full animate-pulse hidden lg:block"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div
          ref={ref}
          className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}
        >
          {/* Main headline - Responsive */}
          <div className="max-w-4xl mb-12 sm:mb-16 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 text-white leading-tight">
              {t('services.title')}
            </h2>
          </div>

          {/* Problems vs Solutions - Stack on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 mb-12 sm:mb-16 md:mb-20">
            {/* Problems */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <div className="h-1 w-12 sm:w-16 bg-red-600"></div>
                <h3 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wider">{t('services.problemsTitle')}</h3>
              </div>
              {t('services.problems', { returnObjects: true }).map((problem, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-zinc-900/50 border-l-4 border-red-600/50 transform transition-all duration-700 delay-${index * 100} ${
                    isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                  }`}
                >
                  <span className="text-red-500 text-lg sm:text-xl mt-1 flex-shrink-0">✕</span>
                  <p className="text-gray-300 text-base sm:text-lg">{problem}</p>
                </div>
              ))}
            </div>

            {/* Solutions */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <div className="h-1 w-12 sm:w-16 bg-green-600"></div>
                <h3 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wider">{t('services.solutionsTitle')}</h3>
              </div>
              {t('services.solutions', { returnObjects: true }).map((solution, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-zinc-900/50 border-l-4 border-green-600/50 transform transition-all duration-700 delay-${index * 100 + 200} ${
                    isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                  }`}
                >
                  <CheckCircle2 className="text-green-500 h-5 w-5 sm:h-6 sm:w-6 mt-1 flex-shrink-0" />
                  <p className="text-gray-300 text-base sm:text-lg">{solution}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Call to action - Responsive */}
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 p-6 sm:p-10 md:p-12 lg:p-16 rounded-none relative overflow-hidden">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }}></div>
            
            <div className="max-w-3xl mx-auto text-center relative z-10">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">{t('services.ctaTitle')}</h3>
              <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-6 sm:mb-8 md:mb-10 leading-relaxed">
                {t('services.ctaDescription')}
              </p>
              
              <Button
                size="lg"
                className="bg-white text-black hover:bg-gray-200 text-base sm:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 rounded-none transition-all duration-300 group w-full sm:w-auto"
                onClick={() => window.voiceflow && window.voiceflow.chat && window.voiceflow.chat.open()}
              >
                <Phone className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                {t('services.ctaButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};